import { useState } from "react";
import { Calendar, Users, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Button from "@/components/ui/Button";

interface AvailabilityWidgetProps {
  variant?: "light" | "dark";
  className?: string;
}

const formatForInput = (d: Date) => d.toISOString().split("T")[0];

export default function AvailabilityWidget({
  variant = "light",
  className = "",
}: AvailabilityWidgetProps) {
  const navigate = useNavigate();

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const [checkIn, setCheckIn] = useState(formatForInput(today));
  const [checkOut, setCheckOut] = useState(formatForInput(tomorrow));
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(0);

  const handleSearch = () => {
    const params = new URLSearchParams({
      checkIn,
      checkOut,
      adults: String(adults),
      children: String(children),
    });
    navigate(`/book?${params.toString()}`);
  };

  const isDark = variant === "dark";
  const bgClass = isDark
    ? "bg-metro-green-950/85 backdrop-blur-md"
    : "bg-white";
  const labelClass = isDark ? "text-white/70" : "text-metro-ink-subtle";
  const valueClass = isDark ? "text-white" : "text-metro-green-950";
  const borderClass = isDark ? "border-white/15" : "border-metro-cream-100";
  const dividerClass = isDark ? "border-white/10" : "border-metro-cream-100";

  return (
    <div className={`rounded-2xl ${bgClass} p-4 shadow-xl md:p-5 ${className}`}>
      <div className="flex flex-col gap-3 sm:grid sm:grid-cols-2 md:grid-cols-[1fr_1fr_1fr_auto] md:gap-0">
        {/* Check-in */}
        <div
          className={`flex items-center gap-3 rounded-xl border ${borderClass} px-4 py-3 md:rounded-none md:border-0 md:border-r`}
        >
          <Calendar
            size={18}
            className={
              isDark
                ? "text-metro-gold shrink-0"
                : "text-metro-green-800 shrink-0"
            }
          />
          <div className="flex-1 min-w-0">
            <label
              className={`block text-[10px] font-medium uppercase tracking-widest-x ${labelClass}`}
            >
              Check In
            </label>
            <input
              type="date"
              value={checkIn}
              min={formatForInput(today)}
              onChange={(e) => {
                setCheckIn(e.target.value);
                if (checkOut <= e.target.value) {
                  const next = new Date(e.target.value);
                  next.setDate(next.getDate() + 1);
                  setCheckOut(formatForInput(next));
                }
              }}
              className={`w-full bg-transparent text-sm font-medium outline-none ${valueClass}`}
            />
          </div>
        </div>

        {/* Check-out */}
        <div
          className={`flex items-center gap-3 rounded-xl border ${borderClass} px-4 py-3 md:rounded-none md:border-0 md:border-r`}
        >
          <Calendar
            size={18}
            className={
              isDark
                ? "text-metro-gold shrink-0"
                : "text-metro-green-800 shrink-0"
            }
          />
          <div className="flex-1 min-w-0">
            <label
              className={`block text-[10px] font-medium uppercase tracking-widest-x ${labelClass}`}
            >
              Check Out
            </label>
            <input
              type="date"
              value={checkOut}
              min={checkIn}
              onChange={(e) => setCheckOut(e.target.value)}
              className={`w-full bg-transparent text-sm font-medium outline-none ${valueClass}`}
            />
          </div>
        </div>

        {/* Guests */}
        <div
          className={`flex items-center gap-3 rounded-xl border ${borderClass} px-4 py-3 md:rounded-none md:border-0`}
        >
          <Users
            size={18}
            className={
              isDark
                ? "text-metro-gold shrink-0"
                : "text-metro-green-800 shrink-0"
            }
          />
          <div className="flex-1 min-w-0">
            <label
              className={`block text-[10px] font-medium uppercase tracking-widest-x ${labelClass}`}
            >
              Guests
            </label>
            <div className="flex gap-2">
              <select
                value={adults}
                onChange={(e) => setAdults(Number(e.target.value))}
                className={`bg-transparent text-sm font-medium outline-none ${valueClass}`}
                aria-label="Adults"
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n} className="text-metro-ink">
                    {n} {n === 1 ? "Adult" : "Adults"}
                  </option>
                ))}
              </select>
              <select
                value={children}
                onChange={(e) => setChildren(Number(e.target.value))}
                className={`bg-transparent text-sm font-medium outline-none ${valueClass}`}
                aria-label="Children"
              >
                {[0, 1, 2, 3, 4].map((n) => (
                  <option key={n} value={n} className="text-metro-ink">
                    {n} {n === 1 ? "Child" : "Children"}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Divider — only visible on mobile between guests and button */}
        <div className={`border-t ${dividerClass} sm:hidden`} />

        {/* Search button */}
        <div className="flex items-center md:pl-3">
          <Button
            size="md"
            onClick={handleSearch}
            leftIcon={<Search size={16} />}
            fullWidth
            className="md:w-auto"
          >
            Check Availability
          </Button>
        </div>
      </div>
    </div>
  );
}